import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { XCircle } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const QUICK_REASONS = [
  "Low quality",
  "Wrong format",
  "Not following instructions",
  "Copyright issue",
  "Spam",
];

/**
 * RejectClipDialog — REQUIRED when admins/mods reject a clip.
 * Forces a reason (via free text or quick-pick) before letting the action go through.
 */
export function RejectClipDialog({ open, onOpenChange, submission, onConfirm }) {
  const [quick, setQuick] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setQuick("");
      setReason("");
      setBusy(false);
    }
  }, [open]);

  function pickQuick(value) {
    setQuick(value);
    setReason((prev) => (prev.trim() ? prev : value));
  }

  async function handleConfirm() {
    const finalReason = reason.trim();
    if (!finalReason) {
      toast({ title: "Reason required", description: "Please provide a reason before rejecting." });
      return;
    }
    setBusy(true);
    try {
      await onConfirm?.({ reason: finalReason, quick_reason: quick || null });
      onOpenChange?.(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reject-clip-dialog" className="max-w-md border-border/60 bg-card text-card-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <XCircle size={16} className="text-rose-400" />
            Reject clip
          </DialogTitle>
          <DialogDescription>
            A reason is mandatory. The clipper will see this in their notification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Quick reason</label>
            <Select value={quick} onValueChange={pickQuick}>
              <SelectTrigger data-testid="reject-quick-reason" className="border-border/60 bg-background">
                <SelectValue placeholder="Pick a common reason..." />
              </SelectTrigger>
              <SelectContent>
                {QUICK_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Reason for rejection <span className="text-rose-400">*</span></label>
            <Textarea
              data-testid="reject-reason-input"
              placeholder="Enter rejection reason..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[100px] border-border/60 bg-background"
            />
            {!reason.trim() && (
              <div className="text-[11px] text-muted-foreground">Required — cannot submit empty.</div>
            )}
          </div>

          {submission?.video_url && (
            <a
              href={submission.video_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-[#c8ff00] hover:underline"
            >
              Open submitted clip
            </a>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" className="rounded-full" onClick={() => onOpenChange?.(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              data-testid="reject-confirm-button"
              className="rounded-full bg-rose-500 text-white hover:bg-rose-400 disabled:opacity-50"
              disabled={busy || !reason.trim()}
              onClick={handleConfirm}
            >
              {busy ? "Rejecting..." : "Reject clip"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
